import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Shield, Check, X, Star, Trash2 } from "lucide-react";

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState({});
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [claims, setClaims] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    if (!loading) {
      if (!user) nav("/login");
      else if (user.role !== "admin") nav("/dashboard");
    }
  }, [user, loading, nav]);

  const load = async () => {
    try {
      const [s, p, a, c, r] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/businesses", { params: { status: "pending" } }),
        api.get("/admin/businesses", { params: { status: "approved" } }),
        api.get("/admin/claims"),
        api.get("/admin/reviews"),
      ]);
      setStats(s.data); setPending(p.data); setApproved(a.data); setClaims(c.data); setReviews(r.data);
    } catch {}
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (loading || !user || user.role !== "admin") return <div className="p-16 text-center text-slate-500">Loading…</div>;

  const approve = async (id) => { await api.post(`/admin/businesses/${id}/approve`); toast.success("Approved"); load(); };
  const reject = async (id) => { await api.post(`/admin/businesses/${id}/reject`); toast.success("Rejected"); load(); };
  const verify = async (id, val) => { await api.post(`/admin/businesses/${id}/verify`, null, { params: { value: val } }); toast.success(val ? "Verified" : "Unverified"); load(); };
  const feature = async (id, val) => { await api.post(`/admin/businesses/${id}/feature`, null, { params: { value: val } }); toast.success(val ? "Featured" : "Unfeatured"); load(); };
  const del = async (id) => { if (!window.confirm("Delete this listing?")) return; await api.delete(`/admin/businesses/${id}`); toast.success("Deleted"); load(); };
  const delReview = async (id) => { await api.delete(`/admin/reviews/${id}`); toast.success("Deleted"); load(); };
  const approveClaim = async (id) => { await api.post(`/admin/claims/${id}/approve`); toast.success("Ownership granted"); load(); };
  const rejectClaim = async (id) => { await api.post(`/admin/claims/${id}/reject`); toast.success("Claim rejected"); load(); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-emerald-800"><Shield className="w-4 h-4" /> Admin</div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1" style={{ fontFamily: "Outfit" }}>Moderation & Analytics</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        {[
          ["Businesses", stats.businesses],
          ["Pending", stats.pending_businesses, "text-amber-600"],
          ["Reviews", stats.reviews],
          ["Quotes", stats.quotes],
          ["Claims", stats.claims_pending, "text-amber-600"],
          ["Users", stats.users],
        ].map(([k, v, cls]) => (
          <div key={k} className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{k}</div>
            <div className={`text-2xl font-bold ${cls || "text-slate-900"} mt-1`} style={{ fontFamily: "Outfit" }}>{v ?? 0}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="admin-tab-pending">Pending ({pending.length})</TabsTrigger>
          <TabsTrigger value="approved" data-testid="admin-tab-approved">All approved ({approved.length})</TabsTrigger>
          <TabsTrigger value="claims" data-testid="admin-tab-claims">Claims ({claims.filter((c) => c.status === "pending").length})</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="admin-tab-reviews">Reviews ({reviews.filter((r) => r.status === "active").length})</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4">
          {pending.length === 0 ? <div className="p-8 text-center text-slate-500 bg-white rounded-xl border border-slate-200">No pending listings.</div> : (
            <div className="space-y-3">
              {pending.map((b) => (
                <div key={b.business_id} className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <Link to={`/business/${b.slug || b.business_id}`} className="font-semibold text-slate-900 hover:text-emerald-700">{b.name}</Link>
                    <div className="text-xs text-slate-500 mt-1">{b.category} · {b.sector} · {b.phone || b.whatsapp || "no phone"}</div>
                    {b.description && <p className="text-sm text-slate-600 mt-1 line-clamp-2">{b.description}</p>}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => approve(b.business_id)} className="bg-emerald-800 hover:bg-emerald-900" data-testid={`approve-${b.business_id}`}><Check className="w-4 h-4 mr-1" /> Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => reject(b.business_id)} data-testid={`reject-${b.business_id}`}><X className="w-4 h-4 mr-1" /> Reject</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="approved" className="mt-4">
          <div className="space-y-3">
            {approved.map((b) => (
              <div key={b.business_id} className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/business/${b.slug || b.business_id}`} className="font-semibold text-slate-900 hover:text-emerald-700">{b.name}</Link>
                    {b.verified && <Badge className="bg-emerald-700">Verified</Badge>}
                    {b.featured && <Badge className="bg-amber-500">Featured</Badge>}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{b.category} · {b.sector} · ⭐ {b.avg_rating || 0} · {b.views || 0} views</div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => verify(b.business_id, !b.verified)}>{b.verified ? "Unverify" : "Verify"}</Button>
                  <Button size="sm" variant="outline" onClick={() => feature(b.business_id, !b.featured)}><Star className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => del(b.business_id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="claims" className="mt-4">
          {claims.length === 0 ? <div className="p-8 text-center text-slate-500 bg-white rounded-xl border border-slate-200">No claims yet.</div> : (
            <div className="space-y-3">
              {claims.map((c) => (
                <div key={c.claim_id} className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-4">
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900">{c.business_name}</div>
                    <div className="text-xs text-slate-500 mt-1">Claimed by <b>{c.user_name}</b> · {c.user_email}</div>
                    {c.message && <p className="text-sm text-slate-600 mt-1">"{c.message}"</p>}
                  </div>
                  {c.status === "pending" ? (
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => approveClaim(c.claim_id)} className="bg-emerald-800 hover:bg-emerald-900">Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => rejectClaim(c.claim_id)}>Reject</Button>
                    </div>
                  ) : <Badge variant={c.status === "approved" ? "default" : "secondary"}>{c.status}</Badge>}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="reviews" className="mt-4">
          <div className="space-y-3">
            {reviews.filter((r) => r.status === "active").map((r) => (
              <div key={r.review_id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900">{r.user_name}</span>
                    <span className="text-xs text-slate-500">rated {r.rating}★</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-1">{r.comment}</p>
                </div>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => delReview(r.review_id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
